import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

export type VisibilityStatus = 'active_now' | 'scheduled' | 'expired' | 'disabled'

export type HomepageSectionRecord = {
  id: number
  storeId: number
  title: string | null
  slug: string
  subtitle: string | null
  sectionType: 'product_grid' | 'carousel' | 'banner' | 'category_grid'
  sourceType: 'manual_products' | 'category' | 'automatic'
  categoryId: number | null
  isSystemGenerated: boolean
  systemKey: string | null
  sortOrder: number
  isActive: boolean
  startDate: string | null
  endDate: string | null
  visibilityStatus: VisibilityStatus
  manualProductIds?: number[]
  createdAt: string
  updatedAt: string
}

/** Matches shopora_Api `MAX_MANUAL_PRODUCTS`. */
export const MAX_SECTION_PRODUCTS = 24

export type ManualProductInput = { productId: number; sortOrder?: number }

export type HomepageSectionPayload = {
  title?: string | null
  slug?: string
  subtitle?: string | null
  sectionType?: HomepageSectionRecord['sectionType']
  sourceType?: HomepageSectionRecord['sourceType']
  categoryId?: number | null
  manualProducts?: Array<{ productId: number; sortOrder?: number }>
  startDate?: string | null
  endDate?: string | null
  isActive?: boolean
  sortOrder?: number
}

export async function listSections(storeId: number): Promise<{ items: HomepageSectionRecord[] }> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${encodeURIComponent(storeId)}/homepage-sections`)
    return assertSuccess(data) as { items: HomepageSectionRecord[] }
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to load homepage sections')
  }
}

export async function createSection(
  storeId: number,
  payload: HomepageSectionPayload,
): Promise<HomepageSectionRecord> {
  try {
    const { data } = await api.post(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/homepage-sections`,
      payload,
    )
    return assertSuccess(data) as HomepageSectionRecord
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to create section')
  }
}

export async function updateSection(
  storeId: number,
  sectionId: number,
  payload: HomepageSectionPayload,
): Promise<HomepageSectionRecord> {
  try {
    const { data } = await api.patch(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/homepage-sections/${encodeURIComponent(sectionId)}`,
      payload,
    )
    return assertSuccess(data) as HomepageSectionRecord
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to update section')
  }
}

export async function deleteSection(storeId: number, sectionId: number): Promise<void> {
  try {
    const { data } = await api.delete(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/homepage-sections/${encodeURIComponent(sectionId)}`,
    )
    assertSuccess(data)
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to delete section')
  }
}

export async function getSection(
  storeId: number,
  sectionId: number,
): Promise<HomepageSectionRecord> {
  const { items } = await listSections(storeId)
  const row = items.find((s) => s.id === sectionId)
  if (!row) {
    throw new Error('Homepage section not found')
  }
  return row
}

/** Persists manual product membership and order via PATCH (no dedicated products route). */
export async function updateSectionProducts(
  storeId: number,
  sectionId: number,
  manualProducts: ManualProductInput[],
): Promise<HomepageSectionRecord> {
  return updateSection(storeId, sectionId, { manualProducts })
}

export async function reorderSections(
  storeId: number,
  items: Array<{ id: number; sortOrder: number }>,
): Promise<{ items: HomepageSectionRecord[] }> {
  try {
    const { data } = await api.patch(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/homepage-sections/reorder`,
      { items },
    )
    return assertSuccess(data) as { items: HomepageSectionRecord[] }
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to reorder sections')
  }
}
